import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { isPopupWindow, sendOAuthResultToOpener } from '@/lib/oauth-popup'

export function AuthCallbackPage() {
  const navigate = useNavigate()

  useEffect(() => {
    let isCancelled = false

    const handleAuthCallback = async () => {
      // Check for Google calendar connection success/error parameters
      const urlParams = new URLSearchParams(window.location.search)
      const success = urlParams.get('success')
      const error = urlParams.get('error')

      console.log('URL params:', { success, error })

      // Handle integration OAuth callback (Google Calendar, Tasks, etc.)
      if (success && success.endsWith('_connected')) {
        console.log('OAuth success detected')
        
        const channelId = urlParams.get('channelId')
        const provider = success.replace('_connected', '')
        const isPopup = isPopupWindow()
        let windowClosed = false
        
        const closeWindow = () => {
          if (!windowClosed && isPopup) {
            windowClosed = true
            window.close()
          }
        }
        
        // Primary path: Broadcast via Supabase Realtime (works in webapp AND extension iframe!)
        if (channelId) {
          console.log('Broadcasting success via Supabase channel:', channelId)
          
          try {
            const channel = supabase.channel(channelId)
            let broadcastSent = false
            
            channel.subscribe(async (status) => {
              console.log('Channel subscription status:', status)
              
              if (status === 'SUBSCRIBED') {
                try {
                  await channel.send({
                    type: 'broadcast',
                    event: 'integration_complete',
                    payload: {
                      success: true,
                      provider,
                      timestamp: new Date().toISOString()
                    }
                  })
                  
                  broadcastSent = true
                  console.log('✅ Broadcast sent successfully')
                  
                  // Clean up OAuth state
                  localStorage.removeItem('integration_oauth_state')
                  
                  // Close popup after brief delay to ensure broadcast is received
                  setTimeout(() => {
                    closeWindow()
                  }, 500)
                } catch (sendError) {
                  console.error('Failed to send broadcast:', sendError)
                  // Still close the window even if broadcast fails
                  localStorage.removeItem('integration_oauth_state')
                  closeWindow()
                }
              } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                console.error('Channel subscription failed with status:', status)
                channel.unsubscribe()
                // Fall through to postMessage fallback
                if (!broadcastSent) {
                  // Trigger fallback path
                  setTimeout(() => {
                    if (!windowClosed && isPopup) {
                      sendOAuthResultToOpener({
                        success: true,
                        data: { message: success }
                      })
                      localStorage.removeItem('integration_oauth_state')
                      closeWindow()
                    }
                  }, 100)
                }
              }
            })
            
            // Timeout fallback: if broadcast takes too long, fall through to postMessage
            setTimeout(() => {
              if (!broadcastSent && !windowClosed) {
                console.log('Channel subscription timeout, falling back to postMessage')
                channel.unsubscribe()
                if (isPopup) {
                  sendOAuthResultToOpener({
                    success: true,
                    data: { message: success }
                  })
                  localStorage.removeItem('integration_oauth_state')
                  closeWindow()
                }
              }
            }, 3000)
            
            // Return here - the timeout fallback handles channel failures
            // Don't fall through to postMessage to avoid duplicate messages
            return
          } catch (error) {
            console.error('Broadcast failed, falling back to postMessage:', error)
            // Continue to fallback below
          }
        }
        
        // Fallback path: postMessage (only when no channelId, for backward compatibility)
        if (isPopup && !windowClosed) {
          console.log('Running in popup window, sending message to opener (no channel)')
          
          // Send success message to opener
          sendOAuthResultToOpener({
            success: true,
            data: { message: success }
          })
          
          // Clean up OAuth state
          localStorage.removeItem('integration_oauth_state')
          
          // Close popup after a brief delay
          setTimeout(() => {
            closeWindow()
          }, 500)
          return
        }
      }

      // Handle integration OAuth ERROR callback
      if (error) {
        console.log('OAuth error detected:', error)
        
        const channelId = urlParams.get('channelId')
        const isPopup = isPopupWindow()
        let windowClosed = false
        
        const closeWindow = () => {
          if (!windowClosed && isPopup) {
            windowClosed = true
            window.close()
          }
        }
        
        // Primary path: Broadcast error via Supabase Realtime
        if (channelId) {
          console.log('Broadcasting error via Supabase channel:', channelId)
          
          try {
            const channel = supabase.channel(channelId)
            let broadcastSent = false
            
            channel.subscribe(async (status) => {
              console.log('Channel subscription status:', status)
              
              if (status === 'SUBSCRIBED') {
                try {
                  // Map error codes to user-friendly messages
                  const errorMessages: Record<string, string> = {
                    'access_denied': 'Access was denied. Please try again and grant the necessary permissions.',
                    'oauth_failed': 'OAuth authentication failed. Please try again.',
                    'no_code': 'No authorization code received from provider.',
                    'invalid_state': 'Invalid authentication state. Please try again.',
                    'user_id_missing': 'Could not verify user identity. Please sign in again.',
                    'config_id_missing': 'Integration configuration error. Please contact support.',
                    'config_not_found': 'Integration not configured. Please contact support.',
                    'token_exchange_failed': 'Failed to exchange authorization code. Please try again.',
                    'token_storage_failed': 'Failed to store credentials. Please try again.',
                    'unexpected': 'An unexpected error occurred. Please try again.'
                  }
                  
                  const userMessage = errorMessages[error] || `Integration failed: ${error}`
                  
                  await channel.send({
                    type: 'broadcast',
                    event: 'integration_error',
                    payload: {
                      success: false,
                      error: userMessage,
                      errorCode: error,
                      timestamp: new Date().toISOString()
                    }
                  })
                  
                  broadcastSent = true
                  console.log('✅ Error broadcast sent successfully')
                  
                  // Clean up OAuth state
                  localStorage.removeItem('integration_oauth_state')
                  
                  // Close popup after brief delay to ensure broadcast is received
                  setTimeout(() => {
                    closeWindow()
                  }, 500)
                } catch (sendError) {
                  console.error('Failed to send error broadcast:', sendError)
                  // Still close the window even if broadcast fails
                  localStorage.removeItem('integration_oauth_state')
                  closeWindow()
                }
              } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                console.error('Channel subscription failed with status:', status)
                channel.unsubscribe()
                // Fall through to postMessage fallback
                if (!broadcastSent) {
                  setTimeout(() => {
                    if (!windowClosed && isPopup) {
                      sendOAuthResultToOpener({
                        success: false,
                        error: error
                      })
                      localStorage.removeItem('integration_oauth_state')
                      closeWindow()
                    }
                  }, 100)
                }
              }
            })
            
            // Timeout fallback
            setTimeout(() => {
              if (!broadcastSent && !windowClosed) {
                console.log('Channel subscription timeout, falling back to postMessage')
                channel.unsubscribe()
                if (isPopup) {
                  sendOAuthResultToOpener({
                    success: false,
                    error: error
                  })
                  localStorage.removeItem('integration_oauth_state')
                  closeWindow()
                }
              }
            }, 3000)
            
            // Return here - the timeout fallback handles channel failures
            // Don't fall through to postMessage to avoid duplicate messages
            return
          } catch (broadcastError) {
            console.error('Error broadcast failed, falling back to postMessage:', broadcastError)
            // Continue to fallback below
          }
        }
        
        // Fallback path: postMessage (only when no channelId, for backward compatibility)
        if (isPopup && !windowClosed) {
          console.log('Running in popup window, sending error to opener (no channel)')
          
          sendOAuthResultToOpener({
            success: false,
            error: error
          })
          
          // Clean up OAuth state
          localStorage.removeItem('integration_oauth_state')
          
          setTimeout(() => {
            closeWindow()
          }, 500)
          return
        }
        
        // Regular flow (not in popup) - keep existing behavior
        try {
          const { data } = await supabase.auth.getSession()
          if (isCancelled) return
          
          if (data.session) {
            // User is authenticated, show toast
            toast.success('Integration connected successfully!')
            
            // Check for stored OAuth state to reopen the correct dialog
            const storedState = localStorage.getItem('integration_oauth_state')
            if (storedState) {
              try {
                const oauthState = JSON.parse(storedState)
                // Store flag to reopen dialog on settings page
                localStorage.setItem('reopen_integration_dialog', oauthState.integrationType || 'calendar')
              } catch (e) {
                console.error('Failed to parse OAuth state:', e)
              }
              localStorage.removeItem('integration_oauth_state')
            }
            
            navigate('/settings', { replace: true })
            return
          } else {
            // User not authenticated, redirect to login with a message
            toast.success('Integration connected successfully! Please sign in to continue.')
            navigate('/login', { replace: true })
            return
          }
        } catch (err) {
          if (isCancelled) return
          console.error('Session check error:', err)
          toast.error('Failed to verify authentication')
          navigate('/login', { replace: true })
          return
        }
      }

      if (error) {
        console.log('OAuth error detected:', error)
        
        // Check if we're in a popup window
        if (isPopupWindow()) {
          console.log('Running in popup window, sending error to opener')
          
          // Send error message to opener
          sendOAuthResultToOpener({
            success: false,
            error: error
          })
          
          // Clean up OAuth state
          localStorage.removeItem('integration_oauth_state')
          
          // Close popup after a brief delay
          setTimeout(() => {
            window.close()
          }, 500)
          return
        }
        
        // Regular flow (not in popup) - keep existing behavior
        try {
          const { data } = await supabase.auth.getSession()
          if (isCancelled) return
          
          if (data.session) {
            // User is authenticated, show error toast and redirect to settings
            toast.error(error)
            
            // Clean up OAuth state
            localStorage.removeItem('integration_oauth_state')
            
            navigate('/settings', { replace: true })
            return
          } else {
            // User not authenticated, show error and redirect to login
            toast.error(`Integration connection failed: ${error}`)
            navigate('/login', { replace: true })
            return
          }
        } catch (err) {
          if (isCancelled) return
          console.error('Session check error:', err)
          toast.error(error)
          navigate('/login', { replace: true })
          return
        }
      }

      // Regular authentication flow
      try {
        const { data, error: authError } = await supabase.auth.getSession()
        if (isCancelled) return
        
        if (authError) {
          console.error('Auth callback error:', authError)
          navigate('/login?error=auth_callback_failed')
          return
        }

        if (data.session) {
          // Successfully authenticated, redirect to home
          navigate('/', { replace: true })
        } else {
          // No session found, redirect to login
          navigate('/login', { replace: true })
        }
      } catch (error) {
        if (isCancelled) return
        console.error('Unexpected error during auth callback:', error)
        navigate('/login?error=unexpected_error')
      }
    }

    handleAuthCallback()

    // Cleanup function to cancel async operations
    return () => {
      isCancelled = true
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-4 text-lg">Completing sign in...</p>
      </div>
    </div>
  )
}
